import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';

import { useModal } from 'context/ModalContext';
import { actionLog } from 'services/actionlog.service';
import { getPerformanceCategory } from 'utils/helpers';
import { FOCO_VISIVEL } from 'utils/foco';
import Humanograma from 'components/Humanograma';
import { UserAvatar } from 'components/user-avatar';

import { ConfirmacaoDaAcao } from './moldura-da-acao';

interface Props {
  callback: (obj: { id: string; message?: string }) => void;
  id: string;
  status: string | undefined;
  userToChange: string | undefined;
  data?: any;
}

export default function ModalTeamMemberDecrease(props: Props) {
  const modalID = 'team-member-decrease';

  const { openModal, closeModal } = useModal();
  const { callback, id, userToChange, data } = props;

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [defaultPercent, setDefaultPercent] = useState<number | undefined>();
  const [newPercent, setNewPercent] = useState<number | undefined>();

  const member = useMemo(() => {
    return data?.target?.find((x: { _id: string | undefined }) => x._id === userToChange);
  }, [data, userToChange]);

  async function handleSubmit() {
    if (sending || !member || !newPercent) return false;

    setSending(true);

    try {
      const { action } = await actionLog('me', 'task_team_member_change_percent', {
        taskId: data._id,
        percent: newPercent,
        player: userToChange,
      });

      const _success = action.attributes.status === 'OK';

      if (_success) {
        setSending(false);
        callback({ id: 'reload_task' });
        closeModal();

        toast.success(`Alteração realizada com sucesso.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error(`Ocorreu um erro ao registrar sua ação. Por favor, tente novamente em alguns minutos.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        setSending(false);
      }
    } catch (error) {
      toast.error(`Ocorreu um erro ao registrar sua ação. Por favor, tente novamente em alguns minutos.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      setSending(false);
    }
  }

  useEffect(() => {
    if (!!userToChange && data && typeof id === 'string' && typeof openModal === 'string' && openModal === `${modalID}-${id}`) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }

    setNewPercent(undefined);
  }, [id, openModal, data, userToChange]);

  useEffect(() => {
    if (modalIsOpen && data && userToChange) {
      setDefaultPercent(data.team?.find((x: { _id: string | undefined }) => x._id === userToChange)?.percent || 0);
    }
  }, [modalIsOpen, data, userToChange]);

  if (!modalIsOpen || !member || !defaultPercent || defaultPercent <= 10) return <></>;

  return (
    <ConfirmacaoDaAcao
      title="Reduzir percentual de atuação"
      size="md"
      onClose={() => {
        if (!sending) closeModal();
      }}
      backDisabled={sending}
      onConfirm={() => {
        handleSubmit();
      }}
      confirmDisabled={sending || !newPercent}
    >
      <p className="m-0">
        Adapte os percentuais, conforme a natureza da realização, a complexidade da atuação e as expectativas específicas.
      </p>

      <dl className="m-0 flex flex-col gap-3 rounded-jj-md bg-surface-100 px-4 py-3.5">
        <div>
          <dt className="text-jj-label font-semibold text-content">Título da Realização Destaque:</dt>
          <dd className="m-0 mt-1 font-semibold text-brand-blue-dark">{data.title}.</dd>
        </div>
        <div>
          <dt className="text-jj-label font-semibold text-content">Participante:</dt>
          <dd className="m-0 mt-1 flex flex-wrap items-center gap-3">
            <span className="flex flex-1 items-center gap-3">
              <UserAvatar data={{ ...member, ...member.extra }} />
              <Humanograma data={{ ...member.extra, _id: member._id, nome: member.name }}>
                <span className="text-content">
                  <span className="font-semibold">{member.name.split(' ')[0]}</span> ({member._id})
                </span>
              </Humanograma>
            </span>
            <span className="rounded-jj-pill bg-brand-yellow-light px-[13px] py-[5px] font-display text-xs font-bold text-brand-blue-dark">
              {defaultPercent}% de atuação
            </span>
          </dd>
        </div>
      </dl>

      <div className="flex flex-col gap-2.5">
        <p id="novo-percentual-de-atuacao" className="m-0 font-semibold">
          Novo percentual de atuação:
        </p>
        <div role="group" aria-labelledby="novo-percentual-de-atuacao" className="flex flex-row items-center gap-2">
          {[25, 50, 75, 100].map(buttonValue => {
            if (defaultPercent && buttonValue >= defaultPercent) return null;

            const escolhido = buttonValue === newPercent;

            return (
              <Tippy
                key={buttonValue}
                placement="bottom"
                allowHTML={true}
                className="bg-black"
                maxWidth={300}
                content={<div>{getPerformanceCategory(buttonValue)}</div>}
              >
                <button
                  type="button"
                  aria-pressed={escolhido}
                  onClick={() => setNewPercent(buttonValue)}
                  className={`flex h-11 min-w-[44px] items-center justify-center rounded-jj-md border-2 px-2 font-display text-sm font-extrabold ${
                    escolhido
                      ? 'border-brand-blue bg-brand-blue text-brand-white'
                      : 'border-surface-200 bg-brand-white text-brand-blue-dark'
                  } ${FOCO_VISIVEL}`}
                >
                  {escolhido ? buttonValue + '%' : buttonValue}
                </button>
              </Tippy>
            );
          })}
        </div>
        <div className="text-brand-blue">{newPercent ? getPerformanceCategory(newPercent) : ''}</div>
      </div>
    </ConfirmacaoDaAcao>
  );
}
